// src/ActivityTimeline.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FILTER,
  getActivityExecutionId,
  getActivityTitle,
  getDelegationDetails,
  mergeRunningToolActivity,
  projectDelegationLifecycle
} from "@photon/chat-core";
import {
  ChevronDown,
  CircleDashed,
  CircleDot,
  CirclePlay,
  Database,
  Focus,
  GitBranch,
  List,
  ListChecks,
  ListOrdered,
  Loader,
  ShieldCheck,
  Sun,
  TriangleAlert,
  UserCheck,
  Users,
  Waypoints,
  Workflow
} from "lucide-react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var FAILED_STATUSES = /* @__PURE__ */ new Set(["error", "failed", "interrupted", "cancelled", "expired"]);
var WAITING_STATUSES = /* @__PURE__ */ new Set(["waiting_approval", "deferred", "unresolved"]);
var SUCCESS_STATUSES = /* @__PURE__ */ new Set(["completed", "responded", "returned", "success", "approved", "passed"]);
var TRACE_GROUP_PREVIEW = 6;
var TRACE_ICONS = {
  // Cognition
  intent: Focus,
  plan: ListChecks,
  execution_steps: ListOrdered,
  thinking: CircleDashed,
  reasoning_trace: Waypoints,
  // Action
  tool: CirclePlay,
  sources: Database,
  // Orchestration
  workflow: Workflow,
  agent: Users,
  delegation: GitBranch,
  // Governance
  governance: ShieldCheck,
  approval: UserCheck,
  error: TriangleAlert
};
function ActivityEventIcon({ type, size = 14 }) {
  const Icon = TRACE_ICONS[type || ""] || CircleDot;
  return /* @__PURE__ */ jsx(Icon, { size, "aria-hidden": "true", "data-activity-type": type });
}
function buildActivityGraph(items) {
  const delegationGroups = /* @__PURE__ */ new Map();
  items.forEach((item, index) => {
    if (!item.delegation_id) return;
    const group = delegationGroups.get(item.delegation_id) ?? [];
    group.push({ item, index });
    delegationGroups.set(item.delegation_id, group);
  });
  if (delegationGroups.size > 1) {
    return {
      parallel: true,
      nodes: Array.from(delegationGroups, ([delegationId, group]) => {
        const first = group[0];
        const last = group[group.length - 1];
        return {
          key: delegationId,
          itemIndex: last.index,
          groupItems: group.map(({ item, index }) => ({ item, itemIndex: index })),
          item: {
            ...first.item,
            ...last.item,
            task: last.item.task || first.item.task,
            child_agent_name: last.item.child_agent_name || first.item.child_agent_name
          }
        };
      })
    };
  }
  const nodes = [];
  for (let itemIndex = 0; itemIndex < items.length; ) {
    const item = items[itemIndex];
    if (item.type !== "thinking") {
      nodes.push({
        key: `${getActivityRowKeys([item])[0]}:${itemIndex}`,
        item,
        itemIndex
      });
      itemIndex += 1;
      continue;
    }
    let endIndex = itemIndex + 1;
    while (endIndex < items.length && items[endIndex].type === "thinking") endIndex += 1;
    const stackCount = endIndex - itemIndex;
    const latestIndex = endIndex - 1;
    const latestItem = items[latestIndex];
    nodes.push({
      key: `thinking:${itemIndex}:${latestIndex}`,
      item: latestItem,
      itemIndex: latestIndex,
      stackCount: stackCount > 1 ? stackCount : void 0,
      stackItems: stackCount > 1 ? items.slice(itemIndex, endIndex).map((stackItem, offset) => ({
        item: stackItem,
        itemIndex: itemIndex + offset
      })) : void 0
    });
    itemIndex = endIndex;
  }
  return { parallel: false, nodes };
}
function getTraceTone(item) {
  const status = item.status || "";
  if (FAILED_STATUSES.has(status)) return "danger";
  if (WAITING_STATUSES.has(status)) return "waiting";
  if (status === "running") return "running";
  if (SUCCESS_STATUSES.has(status)) return "success";
  return "neutral";
}
function ActivityTraceEvent({
  item,
  itemIndex,
  onSelect
}) {
  const tone = getTraceTone(item);
  const actor = item.child_agent_name || item.agent_name;
  const context = actor || item.tool_name || item.type?.replaceAll("_", " ");
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: `photon-execution-trace__event tone-${tone}`,
      onClick: () => onSelect(itemIndex),
      title: "Open this event in List view",
      children: [
        /* @__PURE__ */ jsx("span", { className: "photon-execution-trace__event-icon", children: /* @__PURE__ */ jsx(ActivityEventIcon, { type: item.type, size: 13 }) }),
        /* @__PURE__ */ jsxs("span", { className: "photon-execution-trace__event-copy", children: [
          /* @__PURE__ */ jsx("strong", { children: getActivityTitle(item) }),
          context && /* @__PURE__ */ jsx("small", { children: context })
        ] }),
        item.status && /* @__PURE__ */ jsxs("span", { className: "photon-execution-trace__status", children: [
          /* @__PURE__ */ jsx("i", {}),
          item.status.replaceAll("_", " ")
        ] })
      ]
    }
  );
}
function ActivityTraceGroup({
  title,
  subtitle,
  items,
  onSelect
}) {
  const [showAll, setShowAll] = useState(false);
  const hidden = items.length - TRACE_GROUP_PREVIEW;
  const visible = showAll || hidden <= 0 ? items : items.slice(0, TRACE_GROUP_PREVIEW);
  return /* @__PURE__ */ jsxs("details", { className: "photon-execution-trace__group", open: true, children: [
    /* @__PURE__ */ jsxs("summary", { children: [
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("strong", { children: title }),
        /* @__PURE__ */ jsx("small", { children: subtitle })
      ] }),
      /* @__PURE__ */ jsx(ChevronDown, { size: 15 })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__events", children: [
      visible.map(({ item, itemIndex }) => /* @__PURE__ */ jsx(
        ActivityTraceEvent,
        {
          item,
          itemIndex,
          onSelect
        },
        `${getActivityRowKeys([item])[0]}:${itemIndex}`
      )),
      hidden > 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "photon-execution-trace__events-more",
          onClick: () => setShowAll((prev) => !prev),
          children: showAll ? "Show fewer steps" : `+${hidden} more steps`
        }
      )
    ] })
  ] });
}
function ActivityExecutionMap({
  items,
  onSelect
}) {
  const graph = useMemo(() => buildActivityGraph(items), [items]);
  const summary = `${items.length} ${items.length === 1 ? "event" : "events"}`;
  return /* @__PURE__ */ jsxs("div", { className: "photon-execution-map", "aria-label": "Activity visual", children: [
    /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__summary", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx(GitBranch, { size: 15 }),
        /* @__PURE__ */ jsx("strong", { children: "Execution trace" })
      ] }),
      /* @__PURE__ */ jsx("span", { children: summary })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace", children: [
      /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__terminal", children: [
        /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: /* @__PURE__ */ jsx(Loader, { size: 14 }) }),
        /* @__PURE__ */ jsx("strong", { children: "Request" })
      ] }),
      graph.parallel ? /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__block", children: [
        /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__parallel-heading", children: [
          /* @__PURE__ */ jsx(GitBranch, { size: 14 }),
          /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsx("strong", { children: "Parallel execution" }),
            /* @__PURE__ */ jsxs("small", { children: [
              graph.nodes.length,
              " delegated workstreams"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "photon-execution-trace__parallel", children: graph.nodes.map((node) => /* @__PURE__ */ jsx(
          ActivityTraceGroup,
          {
            title: node.item.child_agent_name || node.item.agent_name || "Delegated agent",
            subtitle: `${node.groupItems?.length ?? 1} events \xB7 ${(node.item.status || "active").replaceAll("_", " ")}`,
            items: node.groupItems ?? [{ item: node.item, itemIndex: node.itemIndex }],
            onSelect
          },
          node.key
        )) })
      ] }) : /* @__PURE__ */ jsx("div", { className: "photon-execution-trace__blocks", children: graph.nodes.map((node) => /* @__PURE__ */ jsx("div", { className: "photon-execution-trace__block", children: node.stackItems ? /* @__PURE__ */ jsx(
        ActivityTraceGroup,
        {
          title: "Reasoning phase",
          subtitle: `${node.stackItems.length} summaries`,
          items: node.stackItems,
          onSelect
        }
      ) : /* @__PURE__ */ jsx(ActivityTraceEvent, { item: node.item, itemIndex: node.itemIndex, onSelect }) }, node.key)) }),
      /* @__PURE__ */ jsxs("div", { className: "photon-execution-trace__terminal is-response", children: [
        /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: /* @__PURE__ */ jsx(Sun, { size: 14 }) }),
        /* @__PURE__ */ jsx("strong", { children: "Response" })
      ] })
    ] })
  ] });
}
var displayValue = (value) => {
  if (typeof value === "string") return value;
  if (value === void 0 || value === null) return "";
  return JSON.stringify(value, null, 2);
};
function ToolCallDetails({
  code,
  args,
  hint,
  sizeHint,
  resultPreview,
  stdout,
  stderr,
  isRunning = false,
  cancellationLimitation
}) {
  const [showAllOutput, setShowAllOutput] = useState(false);
  const argumentEntries = typeof args === "object" && args !== null && !Array.isArray(args) ? Object.entries(args) : null;
  const stdoutValue = displayValue(stdout);
  const stdoutLines = stdoutValue.split("\n");
  const hiddenOutputLineCount = Math.max(0, stdoutLines.length - 20);
  const visibleOutput = showAllOutput ? stdoutValue : stdoutLines.slice(0, 20).join("\n");
  const details = [
    code && { label: "Code", value: code },
    !isRunning && (sizeHint || resultPreview || hint) && { label: "Result", value: sizeHint || resultPreview || hint }
  ].filter(Boolean);
  return /* @__PURE__ */ jsxs("div", { className: "photon-tool-details", children: [
    isRunning && Boolean(cancellationLimitation) && /* @__PURE__ */ jsx("p", { className: "photon-tool-details__notice", children: displayValue(cancellationLimitation) }),
    !code && args !== void 0 && args !== null && /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("strong", { children: "Query" }),
      /* @__PURE__ */ jsx("div", { className: "photon-tool-details__args", children: argumentEntries?.length === 0 ? /* @__PURE__ */ jsx("span", { children: "No arguments (tool defaults)" }) : argumentEntries ? argumentEntries.map(([key, value]) => /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs("span", { children: [
          key,
          ":"
        ] }),
        /* @__PURE__ */ jsx("span", { children: displayValue(value) })
      ] }, key)) : /* @__PURE__ */ jsx("span", { children: displayValue(args) }) })
    ] }),
    details.map((detail) => /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("strong", { children: detail.label }),
      /* @__PURE__ */ jsx("pre", { className: "photon-tool-details__value", children: displayValue(detail.value) })
    ] }, detail.label)),
    !isRunning && stdoutValue && /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("strong", { children: "Output" }),
      /* @__PURE__ */ jsx("pre", { className: "photon-tool-details__value photon-tool-details__output", children: visibleOutput }),
      hiddenOutputLineCount > 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "photon-tool-details__output-toggle",
          onClick: () => setShowAllOutput((current) => !current),
          children: showAllOutput ? "Show less" : `Show ${hiddenOutputLineCount} more lines`
        }
      )
    ] }),
    !isRunning && Boolean(stderr) && /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("strong", { children: "Stderr" }),
      /* @__PURE__ */ jsx("pre", { className: "photon-tool-details__value error", children: displayValue(stderr) })
    ] })
  ] });
}
function ToolActivityCard({ status, description, ...details }) {
  const isRunning = status === "running";
  const isFailed = ["error", "failed", "interrupted"].includes(status || "");
  const isUnresolved = ["waiting_approval", "deferred", "unresolved"].includes(status || "");
  const statusLabel = isRunning ? "Running\u2026" : isFailed ? "Failed" : isUnresolved ? "Outcome unresolved" : status === "returned" ? "Returned" : "Completed";
  return /* @__PURE__ */ jsxs("div", { className: `photon-tool-card${isRunning ? " running" : ""}${isFailed ? " failed" : ""}${isUnresolved ? " waiting" : ""}`, children: [
    /* @__PURE__ */ jsx("div", { className: "photon-tool-card__status", children: statusLabel }),
    /* @__PURE__ */ jsx("div", { className: "photon-tool-card__body", children: /* @__PURE__ */ jsx(ToolCallDetails, { ...details, hint: details.hint ?? description, isRunning }) })
  ] });
}
function DefaultActivityItem({
  item,
  expanded,
  onToggle
}) {
  const isTool = item.type === "tool";
  const isDelegation = item.type === "delegation";
  const isRunning = isTool && item.status === "running";
  const isFailed = ["error", "failed", "interrupted"].includes(item.status || "");
  const isWaiting = ["waiting_approval", "deferred", "unresolved"].includes(item.status || "");
  const detailValues = [
    item.description,
    item.task,
    item.result,
    item.content,
    item.args,
    item.code,
    item.intents,
    item.steps,
    item.step_summary,
    item.learn
  ].filter((value) => value !== void 0 && value !== null && value !== "");
  const hasDetails = isTool || detailValues.length > 0;
  const executionId = getActivityExecutionId(item);
  const typeClass = ["intent", "plan", "execution_steps", "reasoning_trace", "governance", "error", "approval", "delegation"].includes(item.type || "") ? `type-${item.type}` : "";
  const itemClassName = [
    "photon-activity__item",
    typeClass,
    isRunning && "running",
    isFailed && "failed",
    isWaiting && "waiting"
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs("div", { className: itemClassName, children: [
    /* @__PURE__ */ jsx("span", { className: "photon-activity-event-icon", "data-status": item.type === "error" ? "error" : item.status, children: /* @__PURE__ */ jsx(ActivityEventIcon, { type: item.type }) }),
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        className: "photon-activity__item-header",
        onClick: hasDetails ? onToggle : void 0,
        "aria-expanded": hasDetails ? expanded : void 0,
        children: [
          /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsx("strong", { children: getActivityTitle(item) }),
            item.agent_name && !isDelegation && /* @__PURE__ */ jsxs("small", { children: [
              item.agent_name,
              executionId && /* @__PURE__ */ jsxs("i", { children: [
                " (",
                executionId,
                ")"
              ] })
            ] })
          ] }),
          hasDetails && /* @__PURE__ */ jsx(ChevronDown, { size: 14, className: expanded ? "" : "collapsed" })
        ]
      }
    ),
    isTool && !expanded && (isRunning || isWaiting || item.status === "returned") && /* @__PURE__ */ jsx("small", { className: "photon-activity__status", children: isRunning ? "Running\u2026" : isWaiting ? "Outcome unresolved" : "Returned" }),
    hasDetails && expanded && (isTool ? /* @__PURE__ */ jsx(
      ToolActivityCard,
      {
        status: item.status,
        description: item.description,
        code: item.code,
        args: item.args,
        sizeHint: item.size_hint,
        resultPreview: item.result_preview,
        stdout: item.stdout,
        stderr: item.stderr,
        cancellationLimitation: item.cancellationLimitation || item.cancellation_limitation
      }
    ) : /* @__PURE__ */ jsx("div", { className: "photon-activity__details", children: isDelegation ? /* @__PURE__ */ jsx("div", { className: "photon-activity__fields", children: getDelegationDetails(item).map(({ label, value }) => /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx("strong", { children: label }),
      /* @__PURE__ */ jsx("p", { children: value })
    ] }, label)) }) : item.type === "intent" ? /* @__PURE__ */ jsxs("div", { className: "photon-activity__structured photon-activity__structured--intent", children: [
      /* @__PURE__ */ jsx("strong", { children: Array.isArray(item.intents) && item.intents.length === 1 ? "1 intent detected" : `${Array.isArray(item.intents) ? item.intents.length : 0} intents detected` }),
      /* @__PURE__ */ jsx("ol", { children: (Array.isArray(item.intents) ? item.intents : []).map((intent, index) => {
        const value = intent;
        return /* @__PURE__ */ jsxs("li", { children: [
          /* @__PURE__ */ jsx("b", { children: displayValue(value.action) }),
          value.object ? ` \xB7 ${displayValue(value.object)}` : ""
        ] }, index);
      }) }),
      Array.isArray(item.steps) && item.steps.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("strong", { children: "Steps" }),
        /* @__PURE__ */ jsx("ol", { children: item.steps.map((step, index) => /* @__PURE__ */ jsx("li", { children: displayValue(step) }, index)) })
      ] })
    ] }) : item.type === "plan" || item.type === "execution_steps" ? /* @__PURE__ */ jsx("div", { className: `photon-activity__structured photon-activity__structured--${item.type}`, children: /* @__PURE__ */ jsx("ol", { children: displayValue(item.content).split("\n").filter((line) => line.trim()).map((line, index) => /* @__PURE__ */ jsx("li", { children: line.replace(/^\d+\.\s*/, "") }, index)) }) }) : item.type === "reasoning_trace" ? /* @__PURE__ */ jsxs("div", { className: "photon-activity__structured photon-activity__structured--reasoning", children: [
      displayValue(item.step_summary).split("\n").filter((line) => line.trim()).map((line, index) => /* @__PURE__ */ jsx("p", { children: line }, index)),
      Boolean(item.learn) && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("strong", { children: "Key facts extracted" }),
        displayValue(item.learn).split("\n").filter((line) => line.trim()).map((line, index) => /* @__PURE__ */ jsx("p", { children: line }, index))
      ] })
    ] }) : /* @__PURE__ */ jsx("pre", { children: displayValue(item.description || item.task || item.result || item.content) }) }))
  ] });
}
function getActivityRowKeys(items) {
  const occurrences = /* @__PURE__ */ new Map();
  return items.map((item) => {
    const identity = JSON.stringify([
      item.seq,
      item.type,
      item.agent_id,
      item.agent_name,
      item.tool_call_id,
      item.approval_id,
      item.delegation_id,
      item.status,
      item.title
    ]);
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return `${identity}:${occurrence}`;
  });
}
function ActivityTimeline({
  items,
  toolCalls,
  isStreaming = false,
  expanded: controlledExpanded,
  onExpandedChange,
  variant = "page",
  renderItem
}) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [view, setView] = useState("visual");
  const manuallyToggled = useRef(false);
  const autoOpened = useRef(false);
  const previousStreaming = useRef(false);
  const expanded = controlledExpanded ?? localExpanded;
  const setExpanded = (next) => {
    onExpandedChange?.(next);
    if (controlledExpanded === void 0) setLocalExpanded(next);
  };
  const visibleItems = useMemo(() => projectDelegationLifecycle(
    mergeRunningToolActivity(items ?? [], toolCalls ?? []).filter(FILTER)
  ), [items, toolCalls]);
  const rowKeys = useMemo(() => getActivityRowKeys(visibleItems), [visibleItems]);
  const activeIndex = visibleItems.length > 0 ? visibleItems.length - 1 : null;
  useEffect(() => {
    if (isStreaming && visibleItems.length > 0 && !autoOpened.current && !manuallyToggled.current) {
      autoOpened.current = true;
      setExpanded(true);
    }
  }, [isStreaming, visibleItems.length]);
  useEffect(() => {
    if (!previousStreaming.current && isStreaming) {
      autoOpened.current = false;
      manuallyToggled.current = false;
      setExpandedIndex(null);
    } else if (previousStreaming.current && !isStreaming) {
      manuallyToggled.current = false;
      setExpandedIndex(null);
      setExpanded(false);
    }
    previousStreaming.current = isStreaming;
  }, [isStreaming]);
  useEffect(() => {
    if (isStreaming && !manuallyToggled.current) setExpandedIndex(activeIndex);
    if (visibleItems.length === 0) {
      setExpandedIndex(null);
      manuallyToggled.current = false;
    }
  }, [activeIndex, isStreaming, visibleItems.length]);
  if (visibleItems.length === 0) return null;
  return /* @__PURE__ */ jsxs("section", { className: `photon-activity photon-activity--${variant}`, children: [
    /* @__PURE__ */ jsxs("div", { className: "photon-activity__header", children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          className: "photon-activity__toggle",
          onClick: () => {
            manuallyToggled.current = true;
            setExpanded(!expanded);
          },
          "aria-expanded": expanded,
          children: [
            /* @__PURE__ */ jsx(ChevronDown, { size: 15, className: expanded ? "" : "collapsed" }),
            /* @__PURE__ */ jsx("span", { children: "Activity" })
          ]
        }
      ),
      expanded && /* @__PURE__ */ jsxs("div", { className: "photon-activity__views", role: "tablist", "aria-label": "Activity view", children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            role: "tab",
            "aria-selected": view === "visual",
            className: view === "visual" ? "active" : "",
            onClick: () => setView("visual"),
            children: [
              /* @__PURE__ */ jsx(GitBranch, { size: 13 }),
              " Visual"
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            role: "tab",
            "aria-selected": view === "list",
            className: view === "list" ? "active" : "",
            onClick: () => setView("list"),
            children: [
              /* @__PURE__ */ jsx(List, { size: 13 }),
              " List"
            ]
          }
        )
      ] })
    ] }),
    expanded && (view === "visual" ? /* @__PURE__ */ jsx(
      ActivityExecutionMap,
      {
        items: visibleItems,
        onSelect: (index) => {
          manuallyToggled.current = true;
          setExpandedIndex(index);
          setView("list");
        }
      }
    ) : /* @__PURE__ */ jsx("div", { className: "photon-activity__timeline", children: visibleItems.map((item, index) => {
      const itemProps = {
        item,
        index,
        expanded: expandedIndex === index,
        onToggle: () => {
          manuallyToggled.current = true;
          setExpandedIndex((current) => current === index ? null : index);
        },
        isStreaming,
        isActiveItem: index === activeIndex
      };
      return /* @__PURE__ */ jsx("div", { children: renderItem ? renderItem(itemProps) : /* @__PURE__ */ jsx(DefaultActivityItem, { ...itemProps }) }, rowKeys[index]);
    }) }))
  ] });
}
export {
  ActivityEventIcon,
  ActivityTimeline,
  ToolActivityCard,
  ToolCallDetails,
  buildActivityGraph,
  getActivityRowKeys
};
